import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, filter, map, switchMap } from 'rxjs';
import { Injectable } from '@angular/core';

import { ConverterService, InterceptorUtil, Occ, OCC_USER_ID_ANONYMOUS, OccEndpointsService, USE_CLIENT_TOKEN, UserIdService } from '@spartacus/core';
import { ORDER_HISTORY_NORMALIZER, ORDER_NORMALIZER, OrderHistoryList } from '@spartacus/order/root';

import { CustomProductImageNormalizer } from './custom-product-image-normalizer.service';
import { CustomOrder, CustomConsignment, RefundReason, ReturnEntry, OrderInvoiceFile } from '../models';
import { OrderStatusEnum, SaveOrderProductsEnum } from '../enums';
import { OrderFacade } from '../facades';

@Injectable()
export class CustomOrderService implements OrderFacade {
  constructor(
    private userIdService: UserIdService,
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converter: ConverterService,
    private productImageNormalizer: CustomProductImageNormalizer
  ) {}

  getOrderHistoryList(
    pageSize: number,
    currentPage?: number,
    sort?: string,
    statuses?: OrderStatusEnum[]
  ): Observable<OrderHistoryList> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => {
        const params = {};
        if (pageSize) {
          params['pageSize'] = pageSize.toString();
        }
        if (currentPage) {
          params['currentPage'] = currentPage.toString();
        }
        if (sort) {
          params['sort'] = sort.toString();
        }
        if (statuses) {
          params['statuses'] = statuses.join(',');
        }

        const url = this.occEndpointsService.buildUrl('orderHistory', { urlParams: { userId }, queryParams: params });

        return this.http.get<Occ.OrderHistoryList>(url).pipe(this.converter.pipeable(ORDER_HISTORY_NORMALIZER));
      })
    );
  }

  updateOrderSaveOrderProducts(orderCode: string, status: SaveOrderProductsEnum): Observable<void> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('saveOrderProducts', {
          urlParams: { userId, orderId: orderCode },
          queryParams: { status }
        });
        return this.http.post<void>(url, {});
      })
    );
  }

  getOrderDetails(orderCode: string): Observable<CustomOrder> {
    return this.userIdService.takeUserId(false).pipe(
      switchMap((userId: string) => {
        const url = this.occEndpointsService.buildUrl('orderDetail', { urlParams: { userId, orderId: orderCode } });
        let headers = new HttpHeaders();
        if (userId === OCC_USER_ID_ANONYMOUS) {
          headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
        }
        return this.http.get<Occ.Order>(url, { headers }).pipe(this.converter.pipeable(ORDER_NORMALIZER));
      })
    ) as Observable<CustomOrder>;
  }

  getOrderReturnConsignment(orderCode: string, consignmentCode: string): Observable<CustomConsignment> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('orderReturnForm', {
          urlParams: {
            userId,
            orderId: orderCode,
            consignmentId: consignmentCode
          }
        });
        return this.http.get<CustomConsignment>(url).pipe(
          filter((source) => !!source),
          map((source) => ({
            ...source,
            entries: source.entries.map((entry) => ({
              ...entry,
              orderEntry: {
                ...entry.orderEntry,
                product: {
                  ...entry.orderEntry.product,
                  images: this.productImageNormalizer.normalize(entry.orderEntry.product.images as any)
                }
              }
            }))
          }))
        );
      })
    );
  }

  getOrderReturnReasons(): Observable<RefundReason[]> {
    const url = this.occEndpointsService.buildUrl('orderReturnReasons');
    return this.http.get<RefundReason[]>(url);
  }

  submitOrderReturn(
    orderCode: string,
    consignmentCode: string,
    returnEntries: ReturnEntry[]
  ): Observable<CustomConsignment> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('orderReturnSubmit', {
          urlParams: {
            userId,
            orderId: orderCode,
            consignmentId: consignmentCode
          }
        });
        return this.http.post<CustomConsignment>(url, { returnEntries });
      })
    );
  }

  getInvoiceOrder(orderCode: string): Observable<OrderInvoiceFile> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => {
        const url = this.occEndpointsService.buildUrl('invoiceOrder', {
          urlParams: { userId, orderId: orderCode }
        });

        return this.http.get(url, { observe: 'response', responseType: 'blob' })
          .pipe(
            map((response) => ({
              blob: response.body,
              filename: this.extractFilename(response) ?? orderCode
            }))
          );
      })
    );
  }

  private extractFilename(response: HttpResponse<Blob>): string {
    const contentDisposition = response.headers.get('Content-Disposition');
    const regex = /filename="?(.*?)"?$/;
    const match = regex.exec(contentDisposition || '');
    return match?.[1];
  }
}
